import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { parseJwt } from '@common/utils';
import { usePostHog } from 'posthog-js/react';
import useAuthStore from '@/store/AuthStore';
import { ECookieKey, OrganizationRoles } from '@/enums';
import { useGetOrganizationsOnLogin, useGetUserWithToken } from '@/reactQuery/get';
import useUserStore from '@/store/UserStore';
import Routes from '@/router/routes';
import { AuthData, Organization } from '@/types';

export interface UseAuthenticationOptions {
  /**
   * When true, the hook will NOT navigate away when an auth token is already
   * present, and `processAuthSuccess` will NOT navigate internally after
   * setting up auth. Callers that need to chain a specific navigation after
   * login (e.g. the accept-invitation page) pass true so they can control
   * the final destination themselves.
   */
  skipAutoRedirect?: boolean;
}

export function useAuthentication(options: UseAuthenticationOptions = {}) {
  const { skipAutoRedirect = false } = options;
  const navigate = useNavigate();
  const location = useLocation();
  const [redirectTo, setRedirectTo] = useState('/');
  const [error, setError] = useState<string>('');
  const sessionExpiredRedirectionLink = localStorage.getItem('sessionExpiredRedirectTo');
  const posthog = usePostHog();

  const from = location.state?.from || redirectTo;

  const { authToken, setAuthToken, setRequireTwoFactor } = useAuthStore();
  const { setUser } = useUserStore();
  const { mutateAsync: fetchOrganizations } = useGetOrganizationsOnLogin();
  const { mutateAsync: getUserWithToken } = useGetUserWithToken();

  const getValidOrganization = (organizations: Organization[], userId: string): Organization => {
    const workspace = organizations.find((organization: Organization) =>
      organization.members.some(
        ({ user_id, role }) => user_id === userId && role === OrganizationRoles.OWNER,
      ),
    );

    if (workspace && !workspace.isHidden) {
      return workspace;
    }

    return organizations.find(
      (userWorkspace) =>
        !userWorkspace.isHidden &&
        userWorkspace.members.some(({ user_id }) => user_id === userId),
    );
  };

  // Set login cookies
  const setLoginCookies = (data: AuthData, remember: boolean = true) => {
    const options = { expires: remember ? 14 : 1 };
    const cookiesToSet = {
      [ECookieKey.TOKEN]: data?.token,
      [ECookieKey.FALLBACK_WORKSPACE]: data?.mainWorkspace,
      [ECookieKey.MAIN_ORGANIZATION]: data?._id,
      [ECookieKey.REMEMBER_ME]: remember.toString(),
    };

    Object.entries(cookiesToSet).forEach(([key, value]) => {
      if (value) {
        Cookies.set(key, value, options);
      }
    });
  };

  // Process authentication success and handle navigation.
  //
  // Returns an object describing outcome so callers can chain navigation
  // safely. When `skipAutoRedirect` is true (passed to the hook), this
  // function will NOT navigate internally — the caller is responsible for
  // navigating after auth state is set up. This prevents the previous race
  // where the accept-invitation page would call processAuthSuccess (which
  // navigated to "/") and then try to navigate to the target workspace,
  // landing the user on the dashboard root instead of their invited
  // workspace.
  const processAuthSuccess = async (
    token: string,
    remember: boolean = true,
  ): Promise<{ success: boolean; currentOrganization?: string; currentWorkspace?: string }> => {
    try {
      if (!token) {
        setError('Authentication failed. Please try again.');
        return { success: false };
      }

      // Set token in auth store
      setAuthToken(token);

      // Parse token to get user ID
      const parsedToken = parseJwt(token);
      if (!parsedToken || !parsedToken.userId) {
        setError('Authentication failed. Invalid credentials.');
        return { success: false };
      }

      // Fetch organizations and user data in parallel
      const [{ data: organizations }, userData] = await Promise.all([
        fetchOrganizations(token),
        getUserWithToken(token),
      ]);

      // Store user data in Zustand to avoid duplicate API calls
      if (userData) {
        setUser(userData);
      }

      // Determine current organization and workspace for PostHog identification
      const last_organization = userData?.last_organization;
      const last_workspace = userData?.last_workspace;

      // Get valid workspace
      const validOrganization = getValidOrganization(organizations, parsedToken.userId);
      if (!validOrganization) {
        setError('No valid organization found. Please contact support.');
        return { success: false };
      }

      const { _id, mainWorkspace } = validOrganization || {};

      // Make sure we have values for organization and workspace
      if (!_id) {
        setError('Failed to get organization details. Please contact support.');
        return { success: false };
      }

      if (!mainWorkspace) {
        setError('Failed to get workspace details. Please contact support.');
        return { success: false };
      }

      // Validate last_organization is not hidden before using it
      const lastOrgIsValid =
        last_organization &&
        organizations.some((org) => org._id === last_organization && !org.isHidden);

      const currentOrganization = lastOrgIsValid ? last_organization : _id;
      const currentWorkspace = lastOrgIsValid ? last_workspace || mainWorkspace : mainWorkspace;

      // Identify user with PostHog including organization and workspace context
      if (userData && posthog) {
        try {
          posthog.identify(userData._id, {
            email: userData.email,
            organization_id: currentOrganization,
            workspace_id: currentWorkspace,
            user_role: userData.role,
          });
        } catch {
          // Analytics failure should not break the application
        }
      }

      // Set cookies
      setLoginCookies(
        {
          token,
          _id: currentOrganization,
          mainWorkspace: currentWorkspace,
        },
        remember,
      );

      if (sessionExpiredRedirectionLink) {
        // If session expired redirection page is set, navigate to that page
        if (!skipAutoRedirect) {
          navigate(sessionExpiredRedirectionLink, { replace: true });
        }
        localStorage.removeItem('sessionExpiredRedirectTo');
        return { success: true, currentOrganization, currentWorkspace };
      }

      // Set redirect path
      const redirectPath = `${Routes.organization.path}/${currentOrganization}${Routes.workspace.path}/${currentWorkspace}`;
      setRedirectTo(redirectPath);

      // Navigate — skipped when caller wants to chain its own navigation.
      if (!skipAutoRedirect) {
        navigate(from, { replace: true });
      }
      return { success: true, currentOrganization, currentWorkspace };
    } catch (err) {
      setError('Failed to process authentication. Please try again.');
      console.error('Authentication processing error:', err);
      return { success: false };
    }
  };

  // Handle 2FA required response
  const handleTwoFactorRequired = (userId: string) => {
    setRequireTwoFactor(true);
    return userId;
  };

  // Auto-redirect on token presence. Skipped for callers that opt out
  // (e.g. the accept-invitation page, which must keep the user on the page
  // even when a stale token is present in local cookies).
  useEffect(() => {
    if (!skipAutoRedirect && authToken) {
      navigate(from, { replace: true });
    }
  }, [authToken, navigate, from, skipAutoRedirect]);

  return {
    authToken,
    setAuthToken,
    setLoginCookies,
    processAuthSuccess,
    handleTwoFactorRequired,
    redirectTo,
    from,
    navigate,
    error,
    setError,
  };
}

export default useAuthentication;
